import React, { useEffect, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import { Icon } from "@iconify/react";
import toast from "react-hot-toast";
import { CHAT_ATTACHMENT_TYPES } from "./ChatAttachmentMenu";
import { useWhatsAppMediaSend } from "../../../hooks/useWhatsAppMediaSend";
import {
  WHATSAPP_MEDIA_ACCEPT,
  WHATSAPP_CAPTION_MAX,
  validateWhatsAppMediaFile,
} from "../../../utils/whatsappMedia";

function getTypeMeta(type) {
  return CHAT_ATTACHMENT_TYPES.find((t) => t.key === type) || CHAT_ATTACHMENT_TYPES[0];
}

function MediaPreview({ type, url, filename }) {
  if (!url) return null;

  if (type === "image") {
    return (
      <img src={url} alt={filename || "Preview"} className="wa-media-upload-preview-img" />
    );
  }

  if (type === "video") {
    return (
      <video
        src={url}
        controls
        className="wa-media-upload-preview-video"
        preload="metadata"
      />
    );
  }

  if (type === "audio") {
    return <audio src={url} controls className="wa-media-upload-preview-audio" />;
  }

  return (
    <div className="wa-media-upload-preview-doc">
      <Icon icon="solar:document-text-bold" width={28} />
      <span>{filename || "Document"}</span>
    </div>
  );
}

export default function SendChatMediaModal({
  show,
  type = "image",
  chatId,
  sessionExpired = false,
  onHide,
  onSent,
  onSessionExpired,
}) {
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [caption, setCaption] = useState("");
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState("");
  const [uploadedUrl, setUploadedUrl] = useState(null);
  const [convertVoice, setConvertVoice] = useState(false);

  const meta = getTypeMeta(type);
  const showCaption = type !== "audio";

  const handleSuccess = (sendRes, sendMeta) => {
    toast.success(`${meta.label} sent`);
    onHide?.();
    onSent?.(sendRes, sendMeta);
  };

  const { sendMedia, uploading, sending, progress, busy } = useWhatsAppMediaSend({
    chatId,
    sessionExpired,
    onSessionExpired,
    onSuccess: handleSuccess,
  });

  useEffect(() => {
    if (!show) return;
    setFile(null);
    setCaption("");
    setDragging(false);
    setError("");
    setUploadedUrl(null);
    setConvertVoice(false);
  }, [show, type]);

  const handleFile = (nextFile) => {
    if (!nextFile) return;
    const validation = validateWhatsAppMediaFile(nextFile, type);
    if (!validation.ok) {
      setError(validation.error);
      toast.error(validation.error);
      return;
    }
    setFile(nextFile);
    setUploadedUrl(URL.createObjectURL(nextFile));
    setError("");
  };

  useEffect(() => {
    return () => {
      if (uploadedUrl?.startsWith("blob:")) {
        URL.revokeObjectURL(uploadedUrl);
      }
    };
  }, [uploadedUrl]);

  const handleSend = async () => {
    if (sessionExpired) {
      const msg = "Session expired — use template message instead.";
      setError(msg);
      onSessionExpired?.();
      return;
    }

    if (!file) {
      setError("Select a file to upload");
      return;
    }

    setError("");
    const result = await sendMedia({
      file,
      type,
      caption,
      convertVoice,
    });

    if (!result.ok && result.error) {
      setError(result.error);
    }
  };

  const previewUrl = uploadedUrl;

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      className="wa-media-modal"
      dialogClassName="wa-media-modal-dialog"
    >
      <div className="wa-media-modal-card" style={{ "--wa-media-accent": meta.color }}>
        <div className="wa-media-modal-header">
          <div className="wa-media-modal-title">
            <span className="wa-media-modal-title-icon">
              <Icon icon={meta.icon} width={20} />
            </span>
            <span>{meta.label}</span>
          </div>
          <button
            type="button"
            className="wa-media-modal-close"
            onClick={onHide}
            disabled={busy}
            aria-label="Close"
          >
            <Icon icon="solar:close-circle-linear" width={22} />
          </button>
        </div>

        <div className="wa-media-modal-body">
          {error ? <div className="wa-media-modal-error">{error}</div> : null}

          {previewUrl && file ? (
            <div className="wa-media-upload-preview">
              <MediaPreview type={type} url={previewUrl} filename={file.name} />
            </div>
          ) : (
            <div
              className={`wa-media-dropzone ${dragging ? "dragging" : ""} ${file ? "has-file" : ""}`}
              onDragOver={(e) => {
                e.preventDefault();
                setDragging(true);
              }}
              onDragLeave={() => setDragging(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragging(false);
                handleFile(e.dataTransfer.files?.[0]);
              }}
              onClick={() => !busy && fileInputRef.current?.click()}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  if (!busy) fileInputRef.current?.click();
                }
              }}
              role="button"
              tabIndex={0}
            >
              <div className="wa-media-dropzone-icon">
                <Icon icon="solar:cloud-upload-linear" width={34} />
              </div>
              <div className="wa-media-dropzone-title">Upload File</div>
              <div className="wa-media-dropzone-hint">
                Drag &amp; drop a file here or click to select a file
              </div>
              <span className="wa-media-type-badge">{type.toUpperCase()}</span>
              <input
                ref={fileInputRef}
                type="file"
                className="d-none"
                accept={WHATSAPP_MEDIA_ACCEPT[type] || "*/*"}
                disabled={busy}
                onChange={(e) => handleFile(e.target.files?.[0])}
              />
            </div>
          )}

          {file ? (
            <div className="wa-media-selected-file">
              <span>{file.name}</span>
              <button
                type="button"
                className="wa-media-change-file"
                onClick={() => fileInputRef.current?.click()}
                disabled={busy}
              >
                Change
              </button>
              <input
                ref={fileInputRef}
                type="file"
                className="d-none"
                accept={WHATSAPP_MEDIA_ACCEPT[type] || "*/*"}
                disabled={busy}
                onChange={(e) => handleFile(e.target.files?.[0])}
              />
            </div>
          ) : null}

          {type === "audio" ? (
            <label className="wa-media-voice-note">
              <input
                type="checkbox"
                checked={convertVoice}
                onChange={(e) => setConvertVoice(e.target.checked)}
                disabled={busy}
              />
              <span>Convert to voice note (OGG)</span>
            </label>
          ) : null}

          {(uploading || sending) && (
            <div className="wa-media-progress-wrap">
              <div className="wa-media-progress-track">
                <div
                  className={`wa-media-progress-bar ${progress == null ? "indeterminate" : ""}`}
                  style={progress != null ? { width: `${progress}%` } : undefined}
                />
              </div>
              <span className="wa-media-progress-label">
                {uploading ? "Uploading..." : "Sending..."}
                {progress != null ? ` ${progress}%` : ""}
              </span>
            </div>
          )}
        </div>

        <div className="wa-media-modal-footer">
          {showCaption ? (
            <label className="wa-media-caption-field">
              <span>Caption (optional)</span>
              <textarea
                rows={3}
                value={caption}
                maxLength={WHATSAPP_CAPTION_MAX}
                onChange={(e) => setCaption(e.target.value)}
                placeholder="Add a caption..."
                disabled={busy}
              />
            </label>
          ) : null}

          <div className="wa-media-modal-actions">
            <button
              type="button"
              className="wa-media-send-btn"
              onClick={handleSend}
              disabled={busy || !file || sessionExpired}
            >
              <Icon icon="solar:plain-2-bold" width={18} />
              {uploading ? "Uploading..." : sending ? "Sending..." : "Send"}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
